import React, { useRef, useEffect, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapStyle } from './MapStyles';
import { addMarkers } from './marker'
import { mapAccessToken } from "./Config";
import { useDispatch, useSelector } from "react-redux";
import { processGeoFieldPoints, processGeoFieldValidations } from "./marker/ProcessMarkers";
import { addAllWeatherSources } from "./weatherOverlay";
import { markerPopUpContent } from "./marker/markerModal";

import CustomToolbar from '../../common/CustomToolBar';
import MapSideDrawer from "./sidebar/index";
import SpinnerLoader from "../../ui/Loader/spinner";

mapboxgl.accessToken = mapAccessToken;


const FMMap = ({
    options,
    message,
    conversation,
    mapContainer,
    handleMapAreasClick,
    nomagnifyerCntrl,
    noFullScreencontrol,
    nosettingsCntrl,
    limitedSettingsCntrl,
    nomapsearchCntrl,
    noSideDrawer,
    parentTabId = null,
    inContainer,
    field,
    handleAction,
    mapCssStyles,
}) => {

    const dispatch = useDispatch();
    const mapContainerRef = useRef(null);
    const map = useRef(null);

    const { selectedConversation } = useSelector(state => state.chats);
    const { botWaitLoader } = useSelector(state => state.loader);

    const selectedDomain = useSelector((state) => state.domain.selectedDomain);
    const user = useSelector(state => state.user);
    const isSideBar = useSelector((state) => state.layout.isSideBar);
    const [initializeValues, setInitializeValues] = useState(false);
    const [selectedVesselId, setSelectedVesselId] = useState(null);
    const [mapSettings, setMapSettings] = useState({
        zoom: 1,
        lng: -25.0,
        lat: -20.0,
        mapType: "",
    });

    // console.log("MESSAGE => ", message)
    const geoPoints = useMemo(() => processGeoFieldPoints(message), [message]);
    const geoPointsValidaterArray = useMemo(() => processGeoFieldValidations(message), [message]);

    // console.log("Validated geo fields ==> ", geoPointsValidaterArray)
    const [weatherChecked, setWeatherChecked] = useState(false);
    const markerRefs = useRef(new Map());

    // For download Vessel Image
    const [scopeId, setScopeId] = useState(null);
    const fileScope = "bot";


    useEffect(() => {
        if (selectedConversation) {
            const { botId, userDomain, conversationId } = selectedConversation;
            setScopeId(fileScope === "bot" ? botId : fileScope === "domain" ? userDomain : conversationId);
        }
    }, [selectedConversation]);


    useEffect(() => {
        if (options?.mapOptions) {
            const { mapOptions } = options;
            setMapSettings({
                zoom: mapOptions.region?.zoom || 1,
                lng: mapOptions.region?.longitude || -25.0,
                lat: mapOptions.region?.latitude || -20.0,
                mapType: mapOptions.type === "streetSat" ? "sat" : mapOptions.type,
            });
            setWeatherChecked(mapOptions?.weatherOverlay || false);
            setInitializeValues(true);
        }
    }, [options, dispatch]);


    useEffect(() => {
        if (map.current) {
            const markerGeoPts = geoPoints.filter((pt) => pt.type === "geo_point_field");

            markerGeoPts.forEach((point) => {
                point.isMarkerChecked = point.id === selectedVesselId;
            });
            addMarkers(
                mapboxgl,
                markerGeoPts.reverse(),
                map.current,
                fileScope,
                scopeId,
                user,
                setSelectedVesselId,
                geoPointsValidaterArray,
                markerRefs,
                handleOnAction
            );
        }
    }, [selectedVesselId]); //re-render the markers on seleted


    useEffect(() => {
        if (!initializeValues) return;

        if (!mapboxgl.supported()) {
            alert("Your browser does not support Mapbox GL");
            return;
        }

        let cleanupWheelListener = null;

        if (!map.current) {
            map.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: getMapStyle(mapSettings.mapType),
                center: [mapSettings.lng, mapSettings.lat],
                zoom: mapSettings.zoom,
                minZoom: 2,
                projection: 'mercator',          // Use flat map instead of globe
            });

            map.current.boxZoom.disable();
            map.current.scrollZoom.disable();

            map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false, showZoom: true }));
            map.current.addControl(new mapboxgl.FullscreenControl());

            document.querySelector(".mapboxgl-control-container")?.classList.add("controls-custom-class");

            map.current.on("load", () => {
                // Add marker
                addMarkers(
                    mapboxgl,
                    geoPoints.reverse(),
                    map.current,
                    fileScope,
                    scopeId,
                    user,
                    setSelectedVesselId,
                    geoPointsValidaterArray,
                    markerRefs,
                    handleOnAction
                );

                // Add weather
                if (options?.mapOptions?.weatherOverlay) {
                    addAllWeatherSources(
                        map.current,
                        weatherChecked,
                        options?.mapOptions?.weatherOverlayOptions
                    );
                }

                // Add custom zoom handler on load
                const canvas = map.current.getCanvas();

                const handleWheel = (event) => {
                    event.preventDefault();

                    const zoomStep = 0.4;
                    const currentZoom = map.current.getZoom();
                    const delta = Math.sign(event.deltaY); // -1 for zoom in, +1 for zoom out
                    const newZoom = currentZoom - delta * zoomStep;

                    const rect = canvas.getBoundingClientRect();
                    const mouseX = event.clientX - rect.left;
                    const mouseY = event.clientY - rect.top;

                    const zoomTargetLngLat = map.current.unproject([mouseX, mouseY]);

                    map.current.easeTo({
                        zoom: newZoom,
                        around: zoomTargetLngLat,
                        duration: 300,
                        easing: t => t,
                    });
                };

                canvas.addEventListener("wheel", handleWheel, { passive: false });

                // Save cleanup function
                cleanupWheelListener = () => {
                    canvas.removeEventListener("wheel", handleWheel);
                };
            });
        }

        return () => {
            if (cleanupWheelListener) cleanupWheelListener();
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [initializeValues, geoPoints, mapSettings]);
    
    useEffect(() => {
        if (!map.current || !mapContainerRef.current) return;

        const observer = new ResizeObserver(() => {
            console.log("Map container resized, triggering map.resize()");
            map.current.resize();
        });

        observer.observe(mapContainerRef.current);

        return () => observer.disconnect();
    }, [isSideBar]);

    const handleOnAction = (action, selectedkey, value) => {
        // const messageItem = message.find((item) => Object.keys(item)[0] === value);
        const messageItem = message.find(entry =>
            Object.values(entry)[0].fields.some(
                f => f.id === "terminalUpdateId" && f.value === value
            ));

        if (!messageItem) {
            console.error("No matching message found for key:", value);
            return;
        }

        const currentVessel = Object.values(messageItem)[0];
        const selectedID = currentVessel.fields.find(obj => obj.title === selectedkey)?.id;

        const rowFormatted = {};
        currentVessel.fields.forEach((item) => {
            rowFormatted[item.id] = item.value;
        });

        handleAction(action, { row: rowFormatted, selectedField: selectedID ?? "", }, null, value);
    };

    const handleOnClickAlert = async (alert) => {
        const lat = alert.Latitude;
        const lng = alert.Longitude;
        const markerKey = `${alert["Vessel ID"]}|${alert["KIT Number"]}|${alert["Latitude"]}|"${alert["Longitude"]}`;

        if (!map.current || typeof lat !== "number" || typeof lng !== "number") {
            console.warn("Map not ready or invalid coordinates");
            return;
        }

        const mapInstance = map.current;

        // Close all other popups
        markerRefs.current.forEach((ref, key) => {
            if (ref?.popup && key !== markerKey) {
                ref.popup.remove();
                ref.popup = null;
                console.log(` Removed popup for key: ${key}`);
            }
        });
        
        const ref = markerRefs.current.get(markerKey);
        if (!ref?.marker) {
            console.warn("No marker found for key:", markerKey);
            return;
        }

        // Update marker location if needed
        const currentLngLat = ref.marker.getLngLat();
        if (currentLngLat.lng !== lng || currentLngLat.lat !== lat) {
            ref.marker.setLngLat([lng, lat]);
        }

        // Remove existing popup on this marker
        if (ref.popup) {
            ref.popup.remove();
            ref.popup = null;
        }

        // Fly to location first
        mapInstance.flyTo({
            center: [lng, lat],
            zoom: 8,
            speed: 1.5,
            curve: 1.3,
            easing: (t) => t,
            essential: true,
        });

        mapInstance.once("moveend", async () => {
            const popup = await markerPopUpContent(
                alert,
                fileScope,
                scopeId,
                user,
                mapInstance,
                handleOnAction
            );

            ref.marker.setPopup(popup);
            ref.popup = popup;
            popup.addTo(mapInstance);

            // Nudge map center so popup is nicely visible
            setTimeout(() => {
                const popupEl = document.querySelector(".mapboxgl-popup-content");
                if (!popupEl) return;

                const popupHeight = popupEl.getBoundingClientRect().height;
                const canvas = mapInstance.getCanvas();
                const centerX = canvas.clientWidth / 2;
                const offsetFromBottom = canvas.clientHeight * (1 / 2.2);
                const centerY = canvas.clientHeight - offsetFromBottom + popupHeight / 2;
                const offsetCenter = mapInstance.unproject([centerX, centerY]);

                mapInstance.jumpTo({ center: offsetCenter });
            }, 40);
        });

        setSelectedVesselId(markerKey);
    };


    return (
        <>
            {botWaitLoader && !initializeValues ?
                <SpinnerLoader />
                : initializeValues ? (
                    <div className="flex flex-col pb-4">
                        <div className="flex-grow px-4 rounded-xl shadow-custom-shadow bg-white pt-3">
                            <div className="flex items-center justify-between">
                                <div className="text-size-14 font-semibold text-black">{options.title || ""}</div>
                                <CustomToolbar
                                    options={options}
                                    handleAction={handleAction}
                                />
                            </div>
                            <div
                                id={mapContainer}
                                ref={mapContainerRef}
                                className="relative  h-custom-height w-full rounded-[10px] my-[20px]"
                            >
                                {options.mapOptions?.drawer &&
                                    <MapSideDrawer
                                        options={options}
                                        geoPoints={geoPoints}
                                        selectedDomain={selectedDomain}
                                        geoPointsValidaterArray={geoPointsValidaterArray}
                                        handleOnClickAlert={handleOnClickAlert}
                                    />}
                            </div>
                        </div>
                    </div>
                ) : null}
        </>
    );
};

export default FMMap;
